import React, { useState, useEffect } from 'react';
import { ChevronDown, ChevronUp, Shield } from 'lucide-react';
import { ParticleBackground } from '../components/ParticleBackground';
import { Navbar } from '../components/Navbar/Navbar';

const PrivacyPolicy: React.FC = () => {
  const [animationPhase, setAnimationPhase] = useState(0);
  
  // Force scroll to top on page load
  useEffect(() => {
    // Set document title
    document.title = 'Privacy Policy - MockTrail';
    
    window.scrollTo(0, 0);
    
    // Start animation
    setTimeout(() => setAnimationPhase(1), 1600);
  }, []);

  // State to track expanded sections
  const [expandedSections, setExpandedSections] = useState<{[key: string]: boolean}>({
    overview: true,
    collection: true,
    usage: true,
    disclosure: true,
    tracking: true,
    thirdParty: true,
    security: true,
    children: true,
    dnt: true,
    options: true,
    contact: true,
  });

  // Toggle section expansion
  const toggleSection = (section: string) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };


    // Custom background gradient styles
    const backgroundGradient = {
      background: 'radial-gradient(circle at 10% 20%, rgba(90, 92, 106, 0.05) 0%, rgba(32, 45, 58, 0.01) 81.3%)',
      position: 'relative' as 'relative',
      zIndex: 0,
      overflow: 'hidden' as 'hidden'
    };

    
  // Reusable section component
  const PolicySection = ({ 
    id, 
    title, 
    children 
  }: { 
    id: string, 
    title: string, 
    children: React.ReactNode 
  }) => (
    <div className="mb-8 border-b border-border-light dark:border-border-dark pb-6">
      <button 
        className="flex w-full justify-between items-center mb-4 text-left"
        onClick={() => toggleSection(id)}
      >
        <h2 className="text-xl font-heading font-bold text-text-primary-light dark:text-text-primary-dark">
          {title}
        </h2>
        {expandedSections[id] ? 
          <ChevronUp className="text-text-secondary-light dark:text-text-secondary-dark" /> : 
          <ChevronDown className="text-text-secondary-light dark:text-text-secondary-dark" />
        }
      </button>
      {expandedSections[id] && (
        <div className="text-base text-text-secondary-light dark:text-text-secondary-dark space-y-4">
          {children}
        </div>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-background-light dark:bg-background-dark text-text-primary-light dark:text-text-primary-dark" style={backgroundGradient}>
      {/* Animated gradient elements */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden -z-10">
        <div className="absolute top-[10%] left-[5%] w-[40%] h-[30%] rounded-full bg-primary-light/5 dark:bg-primary-dark/5 blur-[80px] animate-pulse-slow"></div>
        <div className="absolute bottom-[20%] right-[10%] w-[25%] h-[40%] rounded-full bg-secondary-light/5 dark:bg-secondary-dark/5 blur-[80px] animate-pulse-slow" style={{ animationDelay: '1s' }}></div>
        <div className="absolute top-[40%] right-[30%] w-[20%] h-[20%] rounded-full bg-primary-light/3 dark:bg-primary-dark/3 blur-[80px] animate-pulse-slow" style={{ animationDelay: '2s' }}></div>
      </div>
      
      <ParticleBackground />
      <Navbar />
      
      <div className="container mx-auto px-4 py-12 max-w-7xl mt-32">
        <div className={`mb-12 text-center transform transition-all duration-500 ease-out ${
          animationPhase >= 1 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
        }`}>
          <div className="flex justify-center mb-4">
          </div>
          <h1 className="text-4xl font-heading font-bold mb-4">Privacy Policy</h1>
          <p className="text-text-secondary-light dark:text-text-secondary-dark text-base">
            Last updated: May 21, 2025
          </p>
        </div>
        
        <div className={`w-full bg-background-light/50 dark:bg-background-dark/50 backdrop-blur-sm rounded-xl border border-border-light dark:border-border-dark p-4 md:p-6 mb-8 transition-all duration-300 hover:border-primary-light/30 dark:hover:border-primary-dark/30 transform transition-all duration-500 ease-out ${
          animationPhase >= 1 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
        }`} style={{ transitionDelay: '200ms' }}>
          <PolicySection id="overview" title="Overview">
            <p>
              Welcome to Mocktrail ("us", "we", or "our"), an online AI-based interview practice platform, which is a part of Aurivance Technologies LLP. We are committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our platform.
            </p>
            <p>
              Please read this Privacy Policy carefully. If you do not agree with the terms of this Privacy Policy, please do not access the platform.
            </p>
            <p>
              We reserve the right to make changes to this Privacy Policy at any time and for any reason. We will alert you about any changes by updating the "Last Updated" date of this Privacy Policy. You are encouraged to periodically review this Privacy Policy to stay informed of updates. You will be deemed to have been made aware of, will be subject to, and will be deemed to have accepted the changes in any revised Privacy Policy by your continued use of the platform after the date such revised Privacy Policy is posted.
            </p>
          </PolicySection>
          
          <PolicySection id="collection" title="Collection of Your Information">
            <p>
              We may collect information about you in a variety of ways. The information we may collect on the platform includes:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong>Personal Data:</strong> Personally identifiable information, such as your name, email address, and demographic information (such as age, gender, etc.) that you voluntarily give to us when you register with the platform or when you choose to participate in various activities related to the platform, such as online chat and message boards.</li>
              <li><strong>Interview Data:</strong> When you participate in mock interviews, we may collect video and audio recordings of your sessions, as well as any documents or information you share during these sessions. This data is used by our AI to provide you with feedback and analysis.</li>
              <li><strong>Derivative Data:</strong> Information our servers automatically collect when you access the platform, such as your IP address, your browser type, your operating system, your access times, and the pages you have viewed directly before and after accessing the platform.</li>
              <li><strong>Data from Social Networks:</strong> User information from social networking sites, such as Facebook, Google, Instagram, including your name, your social network username, location, gender, birth date, email address, profile picture, and public data for contacts, if you connect your account to such social networks.</li>
              <li><strong>Mobile Device Data:</strong> Device information, such as your mobile device ID, model, and manufacturer, and information about the location of your device, if you access the platform from a mobile device.</li>
              <li><strong>Data From Contests, Giveaways, and Surveys:</strong> Personal and other information you may provide when entering contests or giveaways and/or responding to surveys.</li>
            </ul>
          </PolicySection>
          
          <PolicySection id="usage" title="Use of Your Information">
            <p>
              Having accurate information about you permits us to provide you with a smooth, efficient, and customized experience. Specifically, we may use information collected about you via the platform to:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Create and manage your account.</li>
              <li>Process your mock interview sessions and provide AI-driven feedback and analysis.</li>
              <li>Email you regarding your account or order.</li>
              <li>Fulfill and manage purchases, orders, payments, and other transactions related to the platform.</li>
              <li>Enable user-to-user communications.</li>
              <li>Generate a personal profile about you to make future visits to the platform more personalized.</li>
              <li>Increase the efficiency and operation of the platform.</li>
              <li>Monitor and analyze usage and trends to improve your experience with the platform.</li>
              <li>Notify you of updates to the platform.</li>
              <li>Offer new products, services, and/or recommendations to you.</li>
              <li>Perform other business activities as needed.</li>
              <li>Prevent fraudulent transactions, monitor against theft, and protect against criminal activity.</li>
              <li>Request feedback and contact you about your use of the platform.</li>
              <li>Resolve disputes and troubleshoot problems.</li>
              <li>Respond to product and customer service requests.</li>
              <li>Send you a newsletter.</li>
              <li>Solicit support for the platform.</li>
            </ul>
          </PolicySection>
          
          <PolicySection id="disclosure" title="Disclosure of Your Information">
            <p>
              We may share information we have collected about you in certain situations. Your information may be disclosed as follows:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong>By Law or to Protect Rights:</strong> If we believe the release of information about you is necessary to respond to legal process, to investigate or remedy potential violations of our policies, or to protect the rights, property, and safety of others, we may share your information as permitted or required by any applicable law, rule, or regulation.</li>
              <li><strong>Third-Party Service Providers:</strong> We may share your information with third parties that perform services for us or on our behalf, including payment processing, data analysis, email delivery, hosting services, customer service, and marketing assistance.</li>
              <li><strong>Marketing Communications:</strong> With your consent, or with an opportunity for you to withdraw consent, we may share your information with third parties for marketing purposes, as permitted by law.</li>
              <li><strong>Online Postings:</strong> When you post comments, contributions, or other content to the platform, your posts may be viewed by all users and may be publicly distributed outside the platform in perpetuity.</li>
              <li><strong>Third-Party Advertisers:</strong> We may use third-party advertising companies to serve ads when you visit the platform. These companies may use information about your visits to the platform and other websites that are contained in web cookies in order to provide advertisements about goods and services of interest to you.</li>
              <li><strong>Affiliates:</strong> We may share your information with our affiliates, in which case we will require those affiliates to honor this Privacy Policy. Affiliates include our parent company (Aurivance Technologies LLP) and any subsidiaries, joint venture partners, or other companies that we control or that are under common control with us.</li>
              <li><strong>Business Partners:</strong> We may share your information with our business partners to offer you certain products, services, or promotions.</li>
              <li><strong>Other Third Parties:</strong> We may share your information with advertisers and investors for the purpose of conducting general business analysis. We may also share your information with such third parties for marketing purposes, as permitted by law.</li>
              <li><strong>Sale or Bankruptcy:</strong> If we reorganize or sell all or a portion of our assets, undergo a merger, or are acquired by another entity, we may transfer your information to the successor entity. If we go out of business or enter bankruptcy, your information would be an asset transferred or acquired by a third party. You acknowledge that such transfers may occur and that the transferee may decline to honor commitments we made in this Privacy Policy.</li>
            </ul>
            <p>
              We are not responsible for the actions of third parties with whom you share personal or sensitive data, and we have no authority to manage or control third-party solicitations. If you no longer wish to receive correspondence, emails, or other communications from third parties, you are responsible for contacting the third party directly.
            </p>
          </PolicySection>
          
          <PolicySection id="tracking" title="Tracking Technologies">
            <p>
              <strong>Cookies and Web Beacons:</strong> We may use cookies, web beacons, tracking pixels, and other tracking technologies on the platform to help customize the platform and improve your experience. When you access the platform, your personal information is not collected through the use of tracking technology. Most browsers are set to accept cookies by default. You can remove or reject cookies, but be aware that such action could affect the availability and functionality of the platform.
            </p>
            <p>
              <strong>Website Analytics:</strong> We may also partner with selected third-party vendors, such as Google Analytics, to allow tracking technologies and remarketing services on the platform through the use of first-party cookies and third-party cookies, to, among other things, analyze and track users' use of the platform, determine the popularity of certain content, and better understand online activity. By accessing the platform, you consent to the collection and use of your information by these third-party vendors.
            </p>
          </PolicySection>
          
          <PolicySection id="thirdParty" title="Third-Party Websites">
            <p>
              The platform may contain links to third-party websites and applications of interest, including advertisements and external services, that are not affiliated with us. Once you have used these links to leave the platform, any information you provide to these third parties is not covered by this Privacy Policy, and we cannot guarantee the safety and privacy of your information. Before visiting and providing any information to any third-party websites, you should inform yourself of the privacy policies and practices (if any) of the third party responsible for that website, and should take those steps necessary to, in your discretion, protect the privacy of your information. We are not responsible for the content or privacy and security practices and policies of any third parties, including other sites, services, or applications that may be linked to or from the platform.
            </p>
          </PolicySection>
          
          <PolicySection id="security" title="Security of Your Information">
            <p>
              We use administrative, technical, and physical security measures to help protect your personal information. While we have taken reasonable steps to secure the personal information you provide to us, please be aware that despite our efforts, no security measures are perfect or impenetrable, and no method of data transmission can be guaranteed against any interception or other type of misuse. Any information disclosed online is vulnerable to interception and misuse by unauthorized parties. Therefore, we cannot guarantee complete security if you provide personal information.
            </p>
          </PolicySection>
          
          <PolicySection id="children" title="Policy for Children">
            <p>
              We do not knowingly solicit information from or market to children under the age of 13. If you become aware of any data we have collected from children under age 13, please contact us using the contact information provided below.
            </p>
          </PolicySection>
          
          <PolicySection id="dnt" title="Controls for Do-Not-Track Features">
            <p>
              Most web browsers and some mobile operating systems include a Do-Not-Track ("DNT") feature or setting you can activate to signal your privacy preference not to have data about your online browsing activities monitored and collected. No uniform technology standard for recognizing and implementing DNT signals has been finalized. As such, we do not currently respond to DNT browser signals or any other mechanism that automatically communicates your choice not to be tracked online. If a standard for online tracking is adopted that we must follow in the future, we will inform you about that practice in a revised version of this Privacy Policy.
            </p>
          </PolicySection>
          
          <PolicySection id="options" title="Options Regarding Your Information">
            <p>
              <strong>Account Information:</strong> You may at any time review or change the information in your account or terminate your account by:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Logging into your account settings and updating your account</li>
              <li>Contacting us using the contact information provided below</li>
            </ul>
            <p>
              Upon your request to terminate your account, we will deactivate or delete your account and information from our active databases. However, some information may be retained in our files to prevent fraud, troubleshoot problems, assist with any investigations, enforce our Terms of Use, and/or comply with legal requirements.
            </p>
            <p>
              <strong>Emails and Communications:</strong> If you no longer wish to receive correspondence, emails, or other communications from us, you may opt-out by:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Noting your preferences at the time you register your account with the platform.</li>
              <li>Logging into your account settings and updating your preferences.</li>
              <li>Contacting us using the contact information provided below.</li>
            </ul>
            <p>
              If you no longer wish to receive correspondence, emails, or other communications from third parties, you are responsible for contacting the third party directly.
            </p>
          </PolicySection>
          
          <PolicySection id="contact" title="Contact Us">
            <p>
              If you have questions or comments about this Privacy Policy, please contact us at:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Aurivance Technologies LLP</li>
              <li>Email: support@mocktrail.app, office@aurivancetech.com</li>
              <li>Phone: +91 7004297500, +91 9877253751</li>
              <li>Address: 1035 1st floor, Global City Sector 124, Kharar(Rupnagar), Punjab, India, 140301</li>
              <li>Website: https://mocktrail.app</li>
            </ul>
          </PolicySection>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy; 